import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useDropzone } from "react-dropzone"
import { useFormContext } from "react-hook-form"

import { handleRemoveFile } from "../../store/data/actions"
import NewFile from "./NewFile"
import OldFile from "./OldFile"

import s from "./Upload.less"

interface iUploadProps {
  name: string
  fileCollection: string
  initialFiles?: Array<string>
  fileType?: string | Array<string>
  maxFiles?: number
  label?: string
}

interface iProgress {
  loaded: number
  total: number
}

interface iNewFile {
  file: File
  _id?: string
}

const Upload: FunctionComponent<iUploadProps> = ({
  initialFiles = [],
  fileType = undefined,
  maxFiles = 0,
  name,
  fileCollection,
  label = "Upload File",
}) => {
  const [files, setFiles] = useState<Array<iNewFile>>([])
  const [oldFiles, setOldFiles] = useState<string[]>(
    initialFiles.filter((f) => f !== ""),
  )

  const removedFiles = useRef<string[]>([])

  const {
    getValues,
    setValue,
    register,
    formState: { isSubmitting },
  } = useFormContext()

  useEffect(() => {
    register(name)
  }, [name])

  useEffect(() => {
    setOldFiles(initialFiles.filter((f) => f !== ""))
  }, [JSON.stringify(initialFiles)])

  useEffect(() => {
    if (isSubmitting) {
      // removedFiles.current.forEach((_id) => {
      //   handleRemoveFile({ _id, fileCollection })
      // })
      removedFiles.current = []
    }
  }, [isSubmitting])

  const onDrop = useCallback(
    (acceptedFiles) => {
      const map = acceptedFiles.map((f: File) => ({ file: f }))
      setFiles([...files, ...map])
    },
    [files],
  )

  const addFile = (_id: string, fileName: string) => {
    const tmp = files.map((f) =>
      f.file.name === fileName ? { _id, file: f.file } : f,
    )

    const currentVal = getValues(name) || []
    maxFiles === 1
      ? setValue(name, _id, {
          shouldDirty: true,
        })
      : setValue(name, [...currentVal, _id], {
          shouldDirty: true,
        })
    setFiles(tmp)
  }

  const removeFile = (_id: string) => {
    console.log("remove file!")

    const tmp = oldFiles.filter((x) => x !== _id)
    setOldFiles(tmp)
    const currentVal = getValues(name)
    removedFiles.current = [...removedFiles.current, _id]

    console.log(name)

    maxFiles === 1
      ? setValue(name, null, {
          shouldDirty: true,
          shouldValidate: true,
        })
      : setValue(
          name,
          (currentVal || []).filter((x: string) => x !== _id),
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        )
  }

  const removeNewFile = (_id: string) => {
    const tmp = files.filter((f) => f._id !== _id)
    setFiles(tmp)
    const currentVal = getValues(name)
    handleRemoveFile({ _id, fileCollection })

    maxFiles === 1
      ? setValue(name, null, {
          shouldDirty: true,
          shouldValidate: true,
        })
      : setValue(
          name,
          (currentVal || []).filter((x: string) => x !== _id),
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        )
  }

  const maxRemainingFiles = useMemo(
    () => maxFiles - files.length - oldFiles.length,
    [oldFiles.length, files.length],
  )

  const isDropzoneDisabled = useMemo(
    () => maxFiles !== 0 && maxRemainingFiles === 0,
    [files.length, oldFiles.length],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: fileType,
    onDrop,
    maxFiles: maxFiles == 0 ? 0 : maxRemainingFiles,
    multiple: maxFiles !== 1,
    disabled: isDropzoneDisabled,
  })

  return (
    <div>
      <div className={`form-group ${s.dropzoneWrap}`}>
        <label>{label}</label>
        <div
          {...getRootProps()}
          className={`${s.dropzone} ${isDropzoneDisabled ? s.disabled : ""}`}
        >
          <input {...getInputProps()} type="file" />
          {oldFiles.map((file, index) => (
            <OldFile
              key={index}
              fileCollection={fileCollection}
              fileId={file}
              removeFile={removeFile}
            />
          ))}
          {files.map((file, index) => (
            <NewFile
              key={index}
              fileCollection={fileCollection}
              file={file.file}
              addFile={addFile}
              removeFile={removeNewFile}
            />
          ))}
          {/* </div> */}
          {!isDropzoneDisabled &&
            (isDragActive ? (
              <p className={s.dropText}>Drop the file(s).</p>
            ) : (
              <p className={s.dropText}>
                Drag and drop an file here or click to upload.{" "}
                {maxRemainingFiles > 0 && `(${maxRemainingFiles})`}
              </p>
            ))}
        </div>
      </div>
    </div>
  )
}

export default Upload
